import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Alert,
  Divider,
  Paper,
  Chip,
} from "@mui/material";
import { isStopOnMatchEnabled } from "./RuleValidation";

const DAY_LABELS = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

function renderFrequency(frequency) {
  if (!frequency) return "-";

  let parsed = frequency;
  if (typeof frequency === "string") {
    try {
      parsed = JSON.parse(frequency);
    } catch {
      return frequency; // fallback for non-JSON string
    }
  }

  const days = Array.isArray(parsed) ? parsed : parsed?.daysOfWeek;
  if (!Array.isArray(days) || !days.length) return "-";

  const sorted = [...days].sort((a, b) => Number(a?.day || 0) - Number(b?.day || 0));

  return (
    <Box>
      {sorted.map((d, idx) => {
        const slots = Array.isArray(d?.timeSlots) ? d.timeSlots : [];
        const slotText = slots.length
          ? slots
            .map((s) => `${s?.start || "--:--:--"} - ${s?.end || "--:--:--"}`)
            .join(", ")
          : "-";

        return (
          <Typography key={idx} sx={{ color: "#0F172A" }}>
            {DAY_LABELS[d?.day] || `Day ${d?.day}`}: {slotText}
          </Typography>
        );
      })}
    </Box>
  );
}

export default function ReviewAndCreate({
  form,
  errors = {},
  onEditGeneral,
  onEditAssociation,
  onEditOutcome,
  isEditable,
  catalogs = [],
  locations = { stores: [], geoTags: [], customerGroups: [] },
  categories = [],
  products = [],
  skus = [],
  attributeConfig = [],
  configs,
}) {
  const hasErrors = Object.keys(errors)?.length > 0;
  const showStopOnMatch = isStopOnMatchEnabled(configs);

  return (
    <Box sx={{ width: "100%" }}>
      {/* HEADER */}
      <Typography sx={{ fontWeight: 700, fontSize: 24, mb: 3, color: "#1F2937" }}>
        Step 4: Review & Save Rule
      </Typography>

      {/* ERRORS */}
      {hasErrors && (
        <Alert
          severity="error"
          variant="outlined"
          sx={{ mb: 3, borderRadius: 2, fontSize: 14 }}
        >
          <Typography fontWeight={600} mb={1}>
            Please fix the following:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {Object.keys(errors).map((e, idx) => (
              <li key={idx}>
                <Typography fontSize={14}>{errors[e]}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {/* GENERAL DETAILS */}
      <ReviewCard title="General Details" onEdit={onEditGeneral} isEditable={isEditable}>
        <LabelValueGroup
          items={[
            { label: "Rule Type", value: form?.ruleType },
            { label: "Title", value: form?.description },
            { label: "Start Date", value: form?.startDate },
            { label: "End Date", value: form?.endDate },
            { label: "Master Disable", value: String(!!form?.masterDisable) },
            { label: "Priority", value: form?.priority },
            ...(form?.ruleType === "FLASH_SALE"
              ? [{ label: "Frequency", value: renderFrequency(form?.frequency) }]
              : []),
            ...(showStopOnMatch
              ? [{ label: "Stop on Match", value: String(!!form?.stopOnMatch) }]
              : []),
          ]}
        />
      </ReviewCard>

      {/* RULE ASSOCIATION */}
      <ReviewCard title="Rule Association" onEdit={onEditAssociation} isEditable={isEditable}>
        <LabelValueGroup
          items={[
            {
              label: "Catalog",
              value:
                catalogs.find((c) => c.catalogId == form?.catalogId)?.catalogName ||
                form?.catalogId,
            }
          ]}
        />

        <Box mt={3}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1, color: "#374151" }}>
            Match All Conditions
          </Typography>

          {form?.when?.length ? (
            <ConditionList
              conditions={form.when}
              locations={locations}
              categories={categories}
              products={products}
              skus={skus}
              attributeConfig={attributeConfig}
            />
          ) : (
            <EmptyState message="No conditions configured." />
          )}
        </Box>
      </ReviewCard>

      {/* OUTCOME GENERATOR */}
      <ReviewCard title="Outcome Generator" onEdit={onEditOutcome} isEditable={isEditable}>
        {form?.then?.subConditions?.length ? (
          form.then.subConditions.map((block, idx) => (
            <OutcomeBlock
              key={idx}
              block={block}
              locations={locations}
              categories={categories}
              products={products}
              skus={skus}
              attributeConfig={attributeConfig}
            />
          ))
        ) : (
          <EmptyState message="No outcome configured." />
        )}
      </ReviewCard>
    </Box>
  );
}

/* ================= COMPONENTS ================= */

function ReviewCard({ title, children, onEdit, isEditable }) {
  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 3,
        backgroundColor: "#FFFFFF",
        boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
        border: "1px solid #EEF2F7",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid #EEF2F7",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#F1F5F9",
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: "#2563EB",
            letterSpacing: 0.3,
          }}
        >
          {title}
        </Typography>

        {isEditable && (
          <Button
            size="small"
            onClick={onEdit}
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              fontSize: 13,
              borderRadius: 2,
              px: 2,
            }}
          >
            Edit
          </Button>
        )}
      </Box>

      <Box sx={{ p: 3 }}>{children}</Box>
    </Card>
  );
}

function LabelValueGroup({ items }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "1fr 1fr",
          md: "repeat(3,1fr)",
        },
        gap: 2,
      }}
    >
      {items.map((item, i) => (
        <Box
          key={i}
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 2,
            backgroundColor: "#F8FAFC",
          }}
        >
          <Typography
            sx={{
              // fontSize: 13,
              color: "#64748B",
              mb: 0.4,
            }}
          >
            {item.label}
          </Typography>

          <Typography
            sx={{
              // fontSize: 14,
              color: "#0F172A",
            }}
          >
            {item.value || "-"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function ConditionList({
  conditions,
  locations = { stores: [], geoTags: [], customerGroups: [] },
  categories = [],
  products = [],
  skus = [],
  attributeConfig = [],
}) {
  const getIdentity = (condition, item) => {
    if (!item || typeof item !== "object") return item;

    if (condition.attributeFilterType === "storeIds") {
      return item.id ?? item.storeId ?? null;
    }
    if (condition.attributeFilterType === "customerGroups") {
      return item.id ?? null;
    }

    if (condition.attributeType === "CATEGORY") {
      return item.id ?? null;
    }

    if (condition.attributeType === "PRODUCT") {
      return item.id ?? null;
    }

    if (condition.attributeType === "SKU") {
      return item.productItemId ?? null;
    }

    return item[condition.attributeFilterType] ?? item.id ?? null;
  };

  const getName = (condition, item) => {
    if (!item || typeof item !== "object") return "";

    if (condition.attributeFilterType === "storeIds") {
      return item.name ?? item.storeName ?? "";
    }
    return item.name ?? "";
  };

  const getSource = (condition) => {
    const stores = locations?.stores || [];
    const geoTags = locations?.geoTags || [];
    const customerGroups = locations?.customerGroups || [];

    let source = [];

    switch (condition.attributeFilterType) {
      case "storeIds":
        source = stores;
        break;
      case "geoTagIds":
        source = geoTags;
        break;
      case "customerGroups":
        source = customerGroups;
        break;
      default:
        break;
    }

    switch (condition.attributeType) {
      case "CATEGORY":
        source = categories;
        break;
      case "PRODUCT":
        source = products;
        break;
      case "SKU":
        source = skus;
        break;
      default:
        break;
    }
    return source;
  }


  const formatSingleValue = (condition, value) => {
    if (value == null || value === "") return "-";

    const filterType = String(condition?.attributeFilterType || "");
    if (typeof value === "object") {
      if (["storeIds", "customerGroups"].includes(filterType)) {
        return `${getIdentity(condition, value)} - ${getName(condition, value)}`;
      }
      if (filterType != "name") {
        return `${value[filterType]} - ${getName(condition, value)}`;
      } else {
        return `${getIdentity(condition, value)} - ${value[filterType]}`;
      }
    }


    const source = getSource(condition);
    const firstOption = source[0];
    if (["storeIds", "customerGroups", "geoTagIds"].includes(filterType) && source.length > 0) {
      const matched = source.find(item => String(getIdentity(condition, item)) === String(value));
      return matched ? `${getIdentity(condition, matched)} - ${getName(condition, matched)}` : String(value);
    }

    if (firstOption && typeof firstOption === "object" &&
      filterType in firstOption) {
      const matchedByKey = source.find(item => String(item[filterType]) === String(value));
      if (matchedByKey) {
        if (filterType != "name") {
          return `${matchedByKey[filterType]} - ${getName(condition, matchedByKey)}`;
        } else {
          return `${getIdentity(condition, matchedByKey)} - ${matchedByKey[filterType]}`;
        }
      } else {
        return String(value);
      }
    }
    return `${getIdentity(condition, value)}`
  }

  const formatValue = (condition) => {
    const value = condition.attributeFilterValue;

    if (!value) return "-";

    if (Array.isArray(value)) {
      const labels = value
        .map((item) => formatSingleValue(condition, item))
        .filter((label) => label && label !== "-");

      if (!labels.length) return "-";

      return <ChipListValue labels={labels} />;
    }

    return formatSingleValue(condition, value);
  };

  const hasConditionValue = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === "string") {
      return value.trim() !== "";
    }

    if (typeof value === "object") {
      return Object.keys(value).length > 0;
    }

    return true;
  };

  const filtered = conditions?.filter((c) => hasConditionValue(c.attributeFilterValue));

  if (!filtered?.length) return null;

  const getAttributeMetaLabel = (type) => {
    const meta = attributeConfig.find(a => a.attributeType.toLowerCase() === type?.toLowerCase());
    return meta ? meta.label : type;
  };


  return (
    <Box
      sx={{
        mt: 1,
        borderRadius: 2,
        backgroundColor: "#F8FAFC",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          pb: 1,
          mb: 1,
          borderBottom: "1px solid #E2E8F0",
          color: "#64748B",
        }}
      >
        <Box sx={{ flex: 1 }}>Type</Box>
        <Box sx={{ flex: 1 }}>Attribute</Box>
        <Box sx={{ flex: 1 }}>Operator</Box>
        <Box sx={{ flex: 4 }}>Value</Box>
      </Box>

      {filtered.map((c, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            gap: 2,
            py: 1,
            color: "#0F172A",
          }}
        >
          <Box sx={{ flex: 1 }}>{getAttributeMetaLabel(c.attributeType)}</Box>
          <Box sx={{ flex: 1 }}>{c.attributeFilterType}</Box>
          <Box sx={{ flex: 1 }}>{c.attributeFilterClause}</Box>
          <Box sx={{ flex: 4 }}>{formatValue(c)}</Box>
        </Box>
      ))}
    </Box>
  );
}

const CHIP_PREVIEW_LIMIT = 5;

function ChipListValue({ labels }) {
  const [expanded, setExpanded] = useState(false);
  const visibleLabels = expanded ? labels : labels.slice(0, CHIP_PREVIEW_LIMIT);
  const hiddenCount = labels.length - CHIP_PREVIEW_LIMIT;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
      {visibleLabels.map((label, chipIndex) => (
        <Chip
          key={chipIndex}
          label={label}
          sx={{
            maxWidth: "100%",
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />
      ))}

      {hiddenCount > 0 && (
        <Chip
          label={expanded ? "Show less" : `+${hiddenCount} more`}
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            cursor: "pointer",
            fontWeight: 600,
            color: "#2563EB",
            backgroundColor: "#EFF6FF",
            "&:hover": { backgroundColor: "#DBEAFE" },
          }}
        />
      )}
    </Box>
  );
}

function OutcomeBlock({
  block,
  locations = { stores: [], geoTags: [], customerGroups: [] },
  categories = [],
  products = [],
  skus = [],
  attributeConfig = [],
}) {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        mb: 2,
        border: "1px solid"
      }}
    >
      <Typography sx={{ fontSize: 15, mb: 1 }}>
        Conditions
      </Typography>

      {block?.when?.length ? (
        <ConditionList
          conditions={block.when}
          locations={locations}
          categories={categories}
          products={products}
          skus={skus}
          attributeConfig={attributeConfig}
        />
      ) : (
        <EmptyState message="No conditions." />
      )}

      <Divider sx={{ my: 2 }} />

      <Typography sx={{ fontSize: 15, mb: 1 }}>
        Outcome
      </Typography>

      <LabelValueGroup
        items={[
          {
            label: "Discount Type",
            value: block?.then?.discount?.discountType,
          },
          {
            label: "Discount Percentage",
            value: block?.then?.discount?.discountPercentage,
          },
        ]}
      />
    </Box>
  );
}

function EmptyState({ message }) {
  return <Typography color="#6B7280">{message}</Typography>;
}